import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { getLastResults, getDetailResults } from '@/services/casinoApi';
import "./Result.css";
import ResultModalLayout from '@/components/ResultModalLayout';

// Helper function to map win value to display text
const getDisplayText = (win) => {
  if (!win) return "";
  return win;
};

// Helper function to check if win value is a wicket
const isWicket = (win) => {
  if (!win) return false;
  const winStr = win.toString().toLowerCase();
  return winStr === "w" || winStr === "7" || winStr === "wicket" || winStr.includes("wicket");
};

const ResultLucky15 = ({
  data = [],
  gameData = {},
}) => {
  const [showModal, setShowModal] = useState(false);
  const [selectedResult, setSelectedResult] = useState(null);
  const [lastResults, setLastResults] = useState([]);
  const [detailResult, setDetailResult] = useState(null);
  const [loading, setLoading] = useState(false);

  // Fetch last results on mount
  useEffect(() => {
    const fetchLastResults = async () => {
      try {
        const response = await getLastResults('lucky15');
        if (response?.data?.data?.res && Array.isArray(response.data.data.res)) {
          const mappedResults = response.data.data.res.map(item => ({
            mid: item.mid,
            win: item.win || "",
            displayText: getDisplayText(item.win)
          }));
          setLastResults(mappedResults);
        }
      } catch (error) {
        console.error('Error fetching last results:', error);
      }
    };
    
    fetchLastResults();
  }, []);

  const handleResultClick = useCallback(async (result, index) => {
    setSelectedResult(result);
    setLoading(true);
    
    if (lastResults.length === 0) {
      try {
        const response = await getLastResults('lucky15');
        if (response?.data?.data?.res && Array.isArray(response.data.data.res)) {
          const mappedResults = response.data.data.res.map(item => ({
            mid: item.mid,
            win: item.win || "",
            displayText: getDisplayText(item.win)
          }));
          setLastResults(mappedResults);
        }
      } catch (error) {
        console.error('Error fetching last results:', error);
      }
    }
    
    let mid = null;
    if (typeof result === 'object' && result.mid) {
      mid = result.mid;
    } else if (index < lastResults.length) {
      mid = lastResults[index]?.mid;
    } else if (index < data.length + (gameData?.lrs?.length || 0)) {
      mid = gameData?.data?.data?.mid || gameData?.mid;
    }
    
    if (mid && detailResult?.rid !== mid.toString()) {
      try {
        const response = await getDetailResults('lucky15', mid.toString());
        if (response?.data?.data?.t1) {
          setDetailResult(response.data.data.t1);
        }
      } catch (error) {
        console.error('Error fetching detail results:', error);
      }
    }
    
    setLoading(false);
    setShowModal(true);
  }, [lastResults, data, gameData, detailResult]);

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedResult(null);
    setDetailResult(null);
  };

  // Format modal data - memoized to prevent re-renders
  const modalData = useMemo(() => {
    if (!selectedResult || !showModal) return null;

    const resultData = detailResult || (typeof selectedResult === 'object' ? selectedResult.fullData : null);

    if (!resultData) {
      const winValue = typeof selectedResult === 'object' ? selectedResult.win : selectedResult;
      return {
        roundId: gameData?.data?.data?.mid || gameData?.mid || "N/A",
        matchTime: gameData?.data?.data?.mt || new Date().toLocaleString(),
        runText: winValue ? `${winValue} Run${winValue !== "1" ? "s" : ""}` : "",
        win: winValue || "",
      };
    }

    const rdescText = resultData.rdesc || "";

    return {
      roundId: resultData.rid || gameData?.data?.data?.mid || gameData?.mid || "N/A",
      matchTime: resultData.mtime || gameData?.data?.data?.mt || new Date().toLocaleString(),
      runText: rdescText || (resultData.win ? `${resultData.win} Run${resultData.win !== "1" ? "s" : ""}` : ""),
      win: resultData.win || "",
    };
  }, [selectedResult, detailResult, gameData, showModal]);

  // Combine API results with fallback data
  const allResults = lastResults.length > 0
    ? lastResults.map(r => r.win || r.displayText || "")
    : [...data, ...(gameData?.lrs || [])];

  return (
    <>
      <div className="result-row">
        {allResults.map((res, index) => {
          const winValue = typeof res === 'object' ? (res.win || res.displayText || "") : res;
          const displayText = getDisplayText(winValue);
          const resultObj = typeof res === 'object' ? res : (lastResults[index] || { win: res });
          const isWicketResult = isWicket(winValue);

          return (
            <div
              key={index}
              className="result-circle-lucky15"
              onClick={() => handleResultClick(resultObj, index)}
              style={{ cursor: 'pointer' }}
              title={isWicketResult ? "Wicket" : (displayText ? `${displayText} Run${displayText !== "1" ? "s" : ""}` : displayText)}
            >
              {displayText}
            </div>
          );
        })}
      </div>

      {/* Modal */}
      {modalData && (
        <ResultModalLayout
          show={showModal}
          onHide={handleCloseModal}
          title="LUCKY 15 Result"
          roundId={modalData.roundId}
          matchTime={modalData.matchTime}
          loading={loading}
        >
          <div className="clucky15-result">
            <div className="row">
              <div className="col-md-12">
                <h6 className="text-right">
                  <b>Round Id :</b> {modalData.roundId}
                </h6>
              </div>
            </div>
            <div className="row mt-3">
              <img 
              style={{marginRight: 'auto', width: '200px', position: 'relative'}}
              className="lucky15-ball"
                src="https://d3kb8xz339pq18.cloudfront.net/v12/static/backend/images/cards/lucky15/ball-blank.png" 
                alt="Cricket Ball"
                onError={(e) => {
                  e.target.src = "/assets/img/ball-blank.png";
                }}
              />
              <div style={{position: 'absolute', top: '58%', left: '55%', transform: 'translate(-50%, -50%)', fontSize: '26px'}} className="ml-4 ballresult">{modalData.runText || `${modalData.win} Run${modalData.win !== "1" ? "s" : ""}`}</div>
            </div>
          </div>
        </ResultModalLayout>
      )}
    </>
  );
};

export default ResultLucky15;

